import type { FilterModifier } from '../Table/filterModel';

/**
 * Model and pure helpers of `NxFilterBar`.
 *
 * The model is deliberately simpler than `NxTable`'s filter stack: a filter is
 * a SET OF VALUES per field. Values of one field always combine with OR
 * („Status: Offen oder In Arbeit“); the fields combine with the bar's mode —
 * `and` (every active field has to match) or `or` (one is enough). Nothing in
 * here knows a table, so one filter state narrows a list, a board and a
 * calendar alike.
 *
 * Values are compared as strings: `1` and `'1'` are the same value, so a filter
 * survives a round trip through JSON or a URL.
 */

/** One filter value. */
export type NxFilterValue = string | number;

/** How the active fields combine. Values inside one field always combine with OR. */
export type NxFilterBarMode = FilterModifier;

/** Selected values per field key. A field without values is inactive. */
export type NxFilterBarFilters = Record<string, NxFilterValue[]>;

export interface NxFilterBarOption {
  value: NxFilterValue;
  label: string;
  /** Number of entries carrying this value, shown next to it (`countFilterOptions`). */
  count?: number;
}

export interface NxFilterBarField {
  key: string;
  label: string;
  options: NxFilterBarOption[];
  /** Adds a search box above the values — for long lists (customers, projects). */
  searchable?: boolean;
}

/** A saved combination of filters. The caller persists it; the bar only shows it. */
export interface NxFilterView {
  id: string | number;
  name: string;
  filters: NxFilterBarFilters;
  mode: NxFilterBarMode;
  /** `false` hides the delete ×, e.g. for views the application ships. Default `true`. */
  deletable?: boolean;
}

/** What `save-view` hands over: the caller adds the id and stores it. */
export type NxFilterViewDraft = Pick<NxFilterView, 'name' | 'filters' | 'mode'>;

/**
 * The value — or values, for a task with several assignees — a row carries in
 * one field. `null` / `undefined` means no value and matches no option; map
 * „no value“ to an option of its own (`'none'` → „Ohne Projekt“) to filter for it.
 */
export type NxFilterBarValueOf<T> = (row: T, field: string) => NxFilterRowValue;

/** What a row carries in one field: one value, several, or none. */
export type NxFilterRowValue = NxFilterValue | readonly NxFilterValue[] | null | undefined;

/** The filter state `countFilterOptions` counts against. */
export interface NxFilterBarState {
  filters: Readonly<NxFilterBarFilters>;
  mode: NxFilterBarMode;
}

/** A field reduced to what matching needs. */
type CompiledField = { key: string; values: Set<string> };

const valueKey = (value: NxFilterValue): string => String(value);

const isPresent = (value: NxFilterValue | null | undefined): value is NxFilterValue =>
  value !== null && value !== undefined;

/** Distinct keys of a row's raw value in one field. */
function rawKeys(raw: NxFilterRowValue): string[] {
  if (raw === null || raw === undefined) return [];
  if (typeof raw === 'string' || typeof raw === 'number') return [valueKey(raw)];
  return [...new Set(raw.filter(isPresent).map(valueKey))];
}

function fieldMatches(raw: NxFilterRowValue, values: ReadonlySet<string>): boolean {
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string' || typeof raw === 'number') return values.has(valueKey(raw));
  return raw.some((value) => isPresent(value) && values.has(valueKey(value)));
}

/** Keys of the fields that have at least one value, in the order of `filters`. */
export function activeFilterKeys(filters: Readonly<NxFilterBarFilters>): string[] {
  return Object.keys(filters).filter((key) => (filters[key]?.length ?? 0) > 0);
}

function compileFields(filters: Readonly<NxFilterBarFilters>): CompiledField[] {
  return activeFilterKeys(filters).map((key) => ({
    key,
    values: new Set(filters[key].filter(isPresent).map(valueKey)),
  }));
}

/**
 * A copy without empty fields and duplicate values. Used for everything the bar
 * hands out (`save-view`, an applied view), so a caller never stores `[]`.
 */
export function normalizeFilters(filters: Readonly<NxFilterBarFilters>): NxFilterBarFilters {
  const next: NxFilterBarFilters = {};
  for (const key of activeFilterKeys(filters)) {
    const seen = new Set<string>();
    const values = filters[key].filter((value) => {
      if (!isPresent(value) || seen.has(valueKey(value))) return false;
      seen.add(valueKey(value));
      return true;
    });
    if (values.length > 0) next[key] = values;
  }
  return next;
}

function withField(
  filters: Readonly<NxFilterBarFilters>,
  key: string,
  values: NxFilterValue[]
): NxFilterBarFilters {
  const next = { ...filters };
  if (values.length > 0) next[key] = values;
  else delete next[key];
  return next;
}

/** Whether `value` is selected in field `key`. */
export function hasFilterValue(
  filters: Readonly<NxFilterBarFilters>,
  key: string,
  value: NxFilterValue
): boolean {
  return (filters[key] ?? []).some((selected) => valueKey(selected) === valueKey(value));
}

/** New filters with `value` removed from field `key`; an emptied field is dropped. */
export function removeFilterValue(
  filters: Readonly<NxFilterBarFilters>,
  key: string,
  value: NxFilterValue
): NxFilterBarFilters {
  return withField(
    filters,
    key,
    (filters[key] ?? []).filter((selected) => valueKey(selected) !== valueKey(value))
  );
}

/** New filters with `value` ticked in field `key` — or unticked, if it was. */
export function toggleFilterValue(
  filters: Readonly<NxFilterBarFilters>,
  key: string,
  value: NxFilterValue
): NxFilterBarFilters {
  if (hasFilterValue(filters, key, value)) return removeFilterValue(filters, key, value);
  return withField(filters, key, [...(filters[key] ?? []), value]);
}

/** New filters without field `key` („Zurücksetzen“ of one field). */
export function clearFilterField(
  filters: Readonly<NxFilterBarFilters>,
  key: string
): NxFilterBarFilters {
  return withField(filters, key, []);
}

/**
 * Whether a view describes the current state: the same values per field, in
 * any order. The mode only counts from two active fields on — below that it
 * changes no result.
 */
export function isFilterViewActive(
  view: Pick<NxFilterView, 'filters' | 'mode'>,
  filters: Readonly<NxFilterBarFilters>,
  mode: NxFilterBarMode
): boolean {
  const wanted = compileFields(view.filters);
  const current = new Map(compileFields(filters).map((field) => [field.key, field.values]));
  if (wanted.length !== current.size) return false;
  const sameValues = wanted.every(({ key, values }) => {
    const actual = current.get(key);
    return (
      actual !== undefined &&
      actual.size === values.size &&
      [...values].every((value) => actual.has(value))
    );
  });
  return sameValues && (wanted.length < 2 || view.mode === mode);
}

function createMatcher<T>(
  filters: Readonly<NxFilterBarFilters>,
  mode: NxFilterBarMode,
  valueOf: NxFilterBarValueOf<T>
): (row: T) => boolean {
  const fields = compileFields(filters);
  if (fields.length === 0) return () => true;
  const matches = (row: T, field: CompiledField) =>
    fieldMatches(valueOf(row, field.key), field.values);
  return mode === 'or'
    ? (row) => fields.some((field) => matches(row, field))
    : (row) => fields.every((field) => matches(row, field));
}

/** Whether one row passes the filters. Without active fields every row does. */
export function matchesFilterBar<T>(
  row: T,
  filters: Readonly<NxFilterBarFilters>,
  mode: NxFilterBarMode,
  valueOf: NxFilterBarValueOf<T>
): boolean {
  return createMatcher(filters, mode, valueOf)(row);
}

/** The rows that pass the filters, in their order (a new array). */
export function applyFilterBar<T>(
  rows: readonly T[],
  filters: Readonly<NxFilterBarFilters>,
  mode: NxFilterBarMode,
  valueOf: NxFilterBarValueOf<T>
): T[] {
  return rows.filter(createMatcher(filters, mode, valueOf));
}

/**
 * Copies of `fields` whose options carry `count`: how many of `rows` hold that
 * value (a row with several values counts once for each). Pass the rows the
 * filter works on — the scope — not the filtered result.
 *
 * With `state` in `and` mode the counts are faceted: a value counts the rows
 * that pass every OTHER active field, i.e. the hits one would get by ticking it
 * („Kunde Rösch (0)“ while „Abteilung: Programmierung“ is set). A field's own
 * values stay out of its count, so its siblings do not drop to 0 once one is
 * ticked. In `or` mode, and without `state`, the counts are plain.
 */
export function countFilterOptions<T>(
  rows: readonly T[],
  fields: readonly NxFilterBarField[],
  valueOf: NxFilterBarValueOf<T>,
  state?: NxFilterBarState
): NxFilterBarField[] {
  const active = state?.mode === 'and' ? compileFields(state.filters) : [];
  const tallies = new Map(fields.map((field) => [field.key, new Map<string, number>()]));
  for (const row of rows) {
    // A row that fails two active fields counts nowhere; failing exactly one,
    // it still counts for that one field (its facet ignores its own filter).
    let failed: string | null = null;
    let failures = 0;
    for (const field of active) {
      if (fieldMatches(valueOf(row, field.key), field.values)) continue;
      failed = field.key;
      failures += 1;
      if (failures > 1) break;
    }
    if (failures > 1) continue;
    for (const [key, tally] of tallies) {
      if (failures === 1 && key !== failed) continue;
      for (const value of rawKeys(valueOf(row, key))) tally.set(value, (tally.get(value) ?? 0) + 1);
    }
  }
  return fields.map((field) => {
    const tally = tallies.get(field.key);
    return {
      ...field,
      options: field.options.map((option) => ({
        ...option,
        count: tally?.get(valueKey(option.value)) ?? 0,
      })),
    };
  });
}
